import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import Loader from "../../components/Loader";
import { crmHeaders } from "../../utils/crmApi";
import { getResourceConfig } from "../../utils/countryResourcesConfig";
import { getVisibleCountries, hasFullAccess } from "../../utils/userAccess";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const emptyForm = (fields) =>
    fields.reduce((acc, f) => {
        acc[f.name] = "";
        return acc;
    }, {});

function formatDate(value) {
    if (!value) return "";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return d.toLocaleString();
}

function cellValue(row, col) {
    const val = row[col.key];
    if (col.isDate) return formatDate(val);
    if (col.truncate && val && String(val).length > col.truncate) {
        return `${String(val).slice(0, col.truncate)}...`;
    }
    return val ?? "";
}

function rowToForm(type, row) {
    const map = {
        process: { title: row.Title, process: row.Process },
        rebuttles: { title: row.Title, rebuttles: row.Rebuttles },
        "brief-form": { title: row.Title },
        "brand-contact": {
            brandName: row.Brand_Name,
            website: row.Website,
            phone: row.Phone,
            details: row.Details,
        },
        "attorney-details": {
            name: row.Name,
            phone: row.Phone,
            email: row.Email,
            address: row.Address,
            website: row.Website,
        },
        "clearance-report": { title: row.Title },
    };
    return map[type] || {};
}

export default function CountryResourcePage() {
    const { country: countryParam, resourceType } = useParams();
    const country = decodeURIComponent(countryParam || "");
    const config = getResourceConfig(resourceType);

    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [modal, setModal] = useState(null);
    const [form, setForm] = useState({});
    const [selectedFile, setSelectedFile] = useState(null);
    const [saving, setSaving] = useState(false);
    const [viewRow, setViewRow] = useState(null);
    const [countries, setCountries] = useState([]);

    const formFields = useMemo(() => {
        if (!config) return [];
        if (modal === "edit" && config.editFields) return config.editFields;
        return config.fields;
    }, [config, modal]);

    const canAccess = useMemo(() => {
        if (hasFullAccess()) return true;
        const active = countries.filter((c) => c.Status === "Active");
        return getVisibleCountries(active).some((c) => c.Country === country);
    }, [countries, country]);

    const fetchItems = useCallback(() => {
        if (!config || !country) return;
        setLoading(true);
        const q = search.trim() ? `&q=${encodeURIComponent(search.trim())}` : "";
        axios
            .get(
                `${BASE_URL}/country-resources/${resourceType}?country=${encodeURIComponent(country)}${q}`,
                { headers: crmHeaders() }
            )
            .then((res) => setItems(res.data || []))
            .catch(() => toast.error("Failed to load data"))
            .finally(() => setLoading(false));
    }, [config, country, resourceType, search]);

    useEffect(() => {
        axios
            .get(`${BASE_URL}/getcountries`, { headers: crmHeaders() })
            .then((res) => setCountries(res.data || []))
            .catch(() => {});
    }, []);

    useEffect(() => {
        fetchItems();
    }, [fetchItems]);

    const openAdd = () => {
        setForm(emptyForm(config.fields));
        setSelectedFile(null);
        setModal("add");
    };

    const openEdit = (row) => {
        setForm(rowToForm(resourceType, row));
        setSelectedFile(null);
        setModal("edit");
        setViewRow(row);
    };

    const openView = (row) => {
        setViewRow(row);
        setModal("view");
    };

    const closeModal = () => {
        setModal(null);
        setViewRow(null);
        setSelectedFile(null);
        setForm({});
    };

    const handleSave = async (e) => {
        e.preventDefault();
        if (!config) return;

        for (const field of formFields) {
            if (field.required && field.type === "file" && modal === "add" && !selectedFile) {
                toast.error(`${field.label} is required`);
                return;
            }
            if (field.required && field.type !== "file" && !String(form[field.name] || "").trim()) {
                toast.error(`${field.label} is required`);
                return;
            }
        }

        setSaving(true);
        try {
            const useMultipart = config.hasFile;
            let payload;
            let headers = crmHeaders();

            if (useMultipart) {
                const fd = new FormData();
                fd.append("country", country);
                Object.entries(form).forEach(([k, v]) => fd.append(k, v ?? ""));
                if (selectedFile) fd.append("file", selectedFile);
                payload = fd;
                headers = crmHeaders();
                delete headers["Content-Type"];
            } else {
                payload = { ...form, country };
            }

            if (modal === "add") {
                await axios.post(`${BASE_URL}/country-resources/${resourceType}`, payload, { headers });
                toast.success("Added successfully");
            } else {
                await axios.put(
                    `${BASE_URL}/country-resources/${resourceType}/${viewRow.Id}`,
                    payload,
                    { headers }
                );
                toast.success("Updated successfully");
            }
            closeModal();
            fetchItems();
        } catch (err) {
            toast.error(err.response?.data?.message || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (row) => {
        if (!window.confirm("Delete this record?")) return;
        try {
            await axios.delete(`${BASE_URL}/country-resources/${resourceType}/${row.Id}`, {
                headers: crmHeaders(),
            });
            toast.success("Deleted");
            fetchItems();
        } catch {
            toast.error("Delete failed");
        }
    };

    const handleDownload = async (row) => {
        try {
            const res = await axios.get(
                `${BASE_URL}/country-resources/${resourceType}/${row.Id}/download`,
                { headers: crmHeaders(), responseType: "blob" }
            );
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", row.File_Name || "download");
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch {
            toast.error("Download failed");
        }
    };

    if (!config) {
        return (
            <div className="container">
                <h3>Invalid page</h3>
            </div>
        );
    }

    if (!canAccess && countries.length > 0) {
        return (
            <div className="container">
                <h3>Access Denied</h3>
                <p className="text-muted">You do not have access to {country} resources.</p>
            </div>
        );
    }

    if (loading && items.length === 0) return <Loader />;

    return (
        <div className="container">
            <ToastContainer />
            <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
                <h2 className="text-light mb-0">
                    {country} — {config.label}
                </h2>
                <button type="button" className="btn btn-primary" onClick={openAdd}>
                    <i className="bi bi-plus-lg me-1"></i>
                    {config.addLabel}
                </button>
            </div>

            <div className="row mb-3">
                <div className="col-md-6">
                    <input
                        type="text"
                        className="form-control"
                        placeholder="Search..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>
            </div>

            <div className="table-responsive">
                <table className="table table-dark table-striped table-bordered align-middle">
                    <thead>
                        <tr>
                            {config.tableColumns.map((col) => (
                                <th key={col.key}>{col.label}</th>
                            ))}
                            <th style={{ minWidth: 220 }}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((row) => (
                            <tr key={row.Id}>
                                {config.tableColumns.map((col) => (
                                    <td key={col.key}>{cellValue(row, col)}</td>
                                ))}
                                <td>
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-outline-info me-1"
                                        onClick={() => openView(row)}
                                    >
                                        View
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-outline-warning me-1"
                                        onClick={() => openEdit(row)}
                                    >
                                        Edit
                                    </button>
                                    {config.hasFile && (
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-outline-success me-1"
                                            onClick={() => handleDownload(row)}
                                        >
                                            Download
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-outline-danger"
                                        onClick={() => handleDelete(row)}
                                    >
                                        Delete
                                    </button>
                                </td>
                            </tr>
                        ))}
                        {!items.length && (
                            <tr>
                                <td colSpan={config.tableColumns.length + 1} className="text-center text-muted">
                                    No records found.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {modal && modal !== "view" && (
                <div className="modal d-block" style={{ background: "rgba(0,0,0,0.6)" }}>
                    <div className="modal-dialog modal-lg">
                        <div className="modal-content bg-dark text-light">
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    {modal === "add" ? config.addLabel : `Edit ${config.label}`}
                                </h5>
                                <button type="button" className="btn-close btn-close-white" onClick={closeModal}></button>
                            </div>
                            <form onSubmit={handleSave}>
                                <div className="modal-body">
                                    {formFields.map((field) => (
                                        <div className="mb-3" key={field.name}>
                                            <label className="form-label">{field.label}</label>
                                            {field.type === "textarea" ? (
                                                <textarea
                                                    className="form-control"
                                                    rows={4}
                                                    value={form[field.name] || ""}
                                                    onChange={(e) =>
                                                        setForm((f) => ({ ...f, [field.name]: e.target.value }))
                                                    }
                                                    required={field.required}
                                                />
                                            ) : field.type === "file" ? (
                                                <input
                                                    type="file"
                                                    className="form-control"
                                                    accept=".pdf,.xlsx,.xls,.doc,.docx"
                                                    onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                                                    required={field.required && modal === "add"}
                                                />
                                            ) : (
                                                <input
                                                    type={field.type || "text"}
                                                    className="form-control"
                                                    value={form[field.name] || ""}
                                                    onChange={(e) =>
                                                        setForm((f) => ({ ...f, [field.name]: e.target.value }))
                                                    }
                                                    required={field.required}
                                                />
                                            )}
                                        </div>
                                    ))}
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-secondary" onClick={closeModal}>
                                        Cancel
                                    </button>
                                    <button type="submit" className="btn btn-primary" disabled={saving}>
                                        {saving ? "Saving..." : modal === "add" ? "Add" : "Update"}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {modal === "view" && viewRow && (
                <div className="modal d-block" style={{ background: "rgba(0,0,0,0.6)" }}>
                    <div className="modal-dialog modal-lg">
                        <div className="modal-content bg-dark text-light">
                            <div className="modal-header">
                                <h5 className="modal-title">View {config.label}</h5>
                                <button type="button" className="btn-close btn-close-white" onClick={closeModal}></button>
                            </div>
                            <div className="modal-body">
                                {config.tableColumns.map((col) => (
                                    <div className="mb-2" key={col.key}>
                                        <strong>{col.label}:</strong>{" "}
                                        {col.isDate ? formatDate(viewRow[col.key]) : viewRow[col.key]}
                                    </div>
                                ))}
                                {resourceType === "process" && (
                                    <div className="mb-2">
                                        <strong>Full Process:</strong>
                                        <pre className="text-light mt-2" style={{ whiteSpace: "pre-wrap" }}>
                                            {viewRow.Process}
                                        </pre>
                                    </div>
                                )}
                                {resourceType === "rebuttles" && (
                                    <div className="mb-2">
                                        <strong>Full Rebuttles:</strong>
                                        <pre className="text-light mt-2" style={{ whiteSpace: "pre-wrap" }}>
                                            {viewRow.Rebuttles}
                                        </pre>
                                    </div>
                                )}
                                {resourceType === "brand-contact" && viewRow.Details && (
                                    <div className="mb-2">
                                        <strong>Full Details:</strong>
                                        <pre className="text-light mt-2" style={{ whiteSpace: "pre-wrap" }}>
                                            {viewRow.Details}
                                        </pre>
                                    </div>
                                )}
                            </div>
                            <div className="modal-footer">
                                {config.hasFile && (
                                    <button
                                        type="button"
                                        className="btn btn-success"
                                        onClick={() => handleDownload(viewRow)}
                                    >
                                        Download File
                                    </button>
                                )}
                                <button type="button" className="btn btn-secondary" onClick={closeModal}>
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
